import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { __, withLanguage } from '../functions/commom';
import { useInsuranceState } from '../state/state';
import Button from './base/Button';
import Box from './Box';

const useStyles = createUseStyles({
  thankYouContainer: {
    textAlign: 'center',
    padding: '40px 20px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  iconContainer: {
    marginBottom: '30px',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    backgroundColor: 'var(--dai-primary-color)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    color: 'white',
    fontSize: '40px',
    fontWeight: 'bold',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: 'var(--dai-primary-color)',
    marginBottom: '10px',
  },
  subtitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '30px',
  },
  description: {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#666',
    marginBottom: '20px',
    maxWidth: '600px',
    margin: '0 auto 20px auto',
  },
  referenceContainer: {
    backgroundColor: '#f9f9f9',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '40px',
    border: '1px solid #e0e0e0',
  },
  referenceLabel: {
    fontWeight: 'bold',
    color: '#333',
    fontSize: '16px',
    display: 'block',
    marginBottom: '10px',
  },
  referenceNumber: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'var(--dai-primary-color)',
    fontFamily: 'monospace',
    letterSpacing: '1px',
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px dashed var(--dai-primary-color)',
    backgroundColor: 'white',
    transition: 'all 0.2s ease',
    display: 'inline-block',
    position: 'relative',
    '&:hover': {
      backgroundColor: 'var(--dai-primary-color)',
      color: 'white',
      borderStyle: 'solid',
    },
  },
  tooltip: {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#333',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    marginBottom: '5px',
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.2s ease',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '100%',
      left: '50%',
      marginLeft: '-4px',
      borderWidth: '4px',
      borderStyle: 'solid',
      borderColor: '#333 transparent transparent transparent',
    },
  },
  tooltipVisible: {
    opacity: 1,
  },
  copySuccess: {
    backgroundColor: '#4caf50 !important',
    color: 'white !important',
    borderColor: '#4caf50 !important',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    flexWrap: 'wrap',
  },
  homeButton: {
    minWidth: '200px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
  },
});

const InsuranceFormThankYou: React.FC = () => {
  const classes = useStyles();
  const state = useInsuranceState();
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  
  // Get the quote ID from state
  const quoteId = state.quoteId.get();
  const referenceNumber = quoteId || 'REF-' + Date.now();

  const handleCopyReference = async () => {
    try {
      await navigator.clipboard.writeText(referenceNumber);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy reference number:', err);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = referenceNumber;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleBackToHome = () => {
    // Reset form state and go back to home
    state.formStep.set(1);
    state.quoteId.set(null);
    window.location.href = withLanguage('/');
  };

  return (
    <Box>
      <div className={classes.thankYouContainer}>
        <div className={classes.iconContainer}>
          <div className={classes.successIcon}>
            ✓
          </div>
        </div>
        
        <h1 className={classes.title}>
          {__('Thank You for Your Application!')}
        </h1>
        
        <h2 className={classes.subtitle}>
          {__('Application Submitted')}
        </h2>
        
        <p className={classes.description}>
          {__('Your insurance application has been successfully submitted. Our team will review your application and contact you shortly.')}
        </p>
        
        <p className={classes.description}>
          {__('You will receive a confirmation email with your application details within the next few minutes.')}
        </p>
        
        <div className={classes.referenceContainer}>
          <span className={classes.referenceLabel}>
            {__('Reference Number')}
          </span>
          <span 
            className={`${classes.referenceNumber} ${isCopied ? classes.copySuccess : ''}`}
            onClick={handleCopyReference}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            title={isCopied ? __('Copied!') : __('Click to copy')}
          >
            {referenceNumber}
            {isHovered && !isCopied && (
              <span className={`${classes.tooltip} ${classes.tooltipVisible}`}>
                {__('Click to copy')}
              </span>
            )}
            {isCopied && (
              <span className={`${classes.tooltip} ${classes.tooltipVisible}`}>
                {__('Copied!')}
              </span>
            )}
          </span>
        </div>
        
        <div className={classes.buttonContainer}>
          <Button
            onClick={handleBackToHome}
            className={classes.homeButton}
          >
            {__('Back to home')}
          </Button>
        </div>
      </div>
    </Box>
  );
};

export default InsuranceFormThankYou;
